// hrpayroll/src/pages/Profile/NotificationsTab.tsx
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  getMyNotificationPreferences, updateMyNotificationPreferences,
  type NotificationPreferences,
} from "../../api/notifications";

const ALL_ON: NotificationPreferences = {
  email_enabled: true, email_task: true, email_leave: true, email_planner: true,
  email_attendance: true,
  push_enabled: true, push_task: true, push_leave: true, push_planner: true,
  push_attendance: true,
};

const ROWS: { key: "task" | "leave" | "planner" | "attendance"; label: string }[] = [
  { key: "task", label: "Tasks" },
  { key: "leave", label: "Leave" },
  { key: "planner", label: "Daily planner / to-dos" },
  // Your own late-coming warning, plus — if people report to you — an alert
  // when one of them comes in late.
  { key: "attendance", label: "Attendance / late coming" },
];

export default function NotificationsTab() {
  const [prefs, setPrefs] = useState<NotificationPreferences>(ALL_ON);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getMyNotificationPreferences().then(setPrefs).catch(() => setPrefs(ALL_ON)); // silent fallback
  }, []);

  const toggle = (k: keyof NotificationPreferences) =>
    setPrefs((p) => ({ ...p, [k]: !p[k] }));

  const save = async () => {
    setSaving(true);
    try {
      const next = await updateMyNotificationPreferences(prefs);
      setPrefs(next);
      toast.success("Notification preferences saved");
    } catch (err: any) {
      toast.error(err?.message || "Could not save preferences");
    } finally { setSaving(false); }
  };

  return (
    <div className="py-4 max-w-2xl space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">Channels</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Row label="Email notifications" checked={prefs.email_enabled} onChange={() => toggle("email_enabled")} />
          <Row label="Push notifications" checked={prefs.push_enabled} onChange={() => toggle("push_enabled")} />
          <p className="text-xs text-gray-400">
            The in-app notification bell is always on. Email also respects your branch admin's email setting —
            if they turned a category's email off, you can't re-enable it here.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">By type</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 gap-y-3 items-center text-sm">
            <div></div>
            <div className="text-xs text-gray-400 text-center">Email</div>
            <div className="text-xs text-gray-400 text-center">Push</div>
            {ROWS.map((r) => (
              <Row3 key={r.key}
                label={r.label}
                email={prefs[`email_${r.key}` as keyof NotificationPreferences] as boolean}
                push={prefs[`push_${r.key}` as keyof NotificationPreferences] as boolean}
                onEmail={() => toggle(`email_${r.key}` as keyof NotificationPreferences)}
                onPush={() => toggle(`push_${r.key}` as keyof NotificationPreferences)}
                emailDisabled={!prefs.email_enabled}
                pushDisabled={!prefs.push_enabled}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save preferences"}</Button>
      </div>
    </div>
  );
}

function Row({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm text-gray-700">{label}</span>
      <input type="checkbox" checked={checked} onChange={onChange} className="h-4 w-4" />
    </label>
  );
}

function Row3({ label, email, push, onEmail, onPush, emailDisabled, pushDisabled }: {
  label: string; email: boolean; push: boolean; onEmail: () => void; onPush: () => void;
  emailDisabled: boolean; pushDisabled: boolean;
}) {
  return (
    <>
      <div className="text-gray-700">{label}</div>
      <input type="checkbox" className="h-4 w-4 mx-auto" checked={email} disabled={emailDisabled} onChange={onEmail} />
      <input type="checkbox" className="h-4 w-4 mx-auto" checked={push} disabled={pushDisabled} onChange={onPush} />
    </>
  );
}
